import { useEffect, useMemo, useRef } from 'react';

import { CoordinationClient } from '@atlassiansox/engagekit-ts';

import { fg } from '@atlaskit/platform-feature-flags';
import { usePostOfficeExernalMessageProxy } from '@atlassian/post-office-context';
import { CancellablePromise } from '@atlassian/search-client';

import { SPOTLIGHT_MESSAGE_ID } from '../../common/constants/spotlight';
import { useSpotlightState } from '../../controllers/store/spotlight';
import { useCloudId } from '../store/bootstrap/accessors';

export const useSpotlightPrefetch = (): void => {
	const [cloudId] = useCloudId();
	const [spotlight, { start, end }] = useSpotlightState();
	const requestRef = useRef<CancellablePromise<boolean>>();
	const hasRequestedMessageRef = useRef(false);
	const { requestMessageStart, dismissMessage, hasMessageStarted } =
		usePostOfficeExernalMessageProxy(SPOTLIGHT_MESSAGE_ID);

	const coordinationClient = useMemo(() => {
		if (!cloudId || cloudId === '') {
			return {
				start: () => Promise.resolve(false),
				stop: () => Promise.resolve(false),
			};
		}

		const client = new CoordinationClient({
			cloudId,
			stargateUrl: '/gateway/api',
		});

		return client;
	}, [cloudId]);

	useEffect(() => {
		if (!fg('rovo-fps-changeboarding-spotlight-po-migration')) {
			return;
		}

		if (!hasRequestedMessageRef.current) {
			requestMessageStart();
			hasRequestedMessageRef.current = true;
			return;
		}

		// If spotlight should be shown within Post Office logic
		if (hasMessageStarted) {
			start();
		}
	}, [hasMessageStarted, requestMessageStart, start]);

	// Delete the useEffect when cleaning up rovo-fps-changeboarding-spotlight-po-migration
	useEffect(() => {
		if (fg('rovo-fps-changeboarding-spotlight-po-migration')) {
			return;
		}

		if (requestRef.current) {
			return;
		}

		requestRef.current = new CancellablePromise(coordinationClient.start(SPOTLIGHT_MESSAGE_ID));

		requestRef.current
			.promise()
			.then((displaySpotlight) => {
				displaySpotlight ? start() : end();
			})
			.catch(() => {
				end();
			});

		// The network request for the spotlight exposure should only ever be called once.
		// Otherwise, it will cause conflicts and the spotlight will never be rendered.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (spotlight.state === 'dismiss') {
			if (fg('rovo-fps-changeboarding-spotlight-po-migration')) {
				dismissMessage();
			} else {
				coordinationClient.stop(SPOTLIGHT_MESSAGE_ID);
			}
		}
	}, [coordinationClient, dismissMessage, spotlight.state]);
};

export const SpotlightPrefetch = () => {
	useSpotlightPrefetch();
	return null;
};
